import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { EwalletInfo } from '../../../interfaces/ewallet-info';
import TopBackNavBar from '../../../components/BackNavBar';
import { RedButton } from '../../../components/Button';
import EwalletCard from '../EwalletCard';
import getWalletTransaction from '../../../utils/getWalletTransaction';
import { TransactionInfoTax } from '../../../interfaces/transaction-info-tax';
import { formatCurrencyString } from '../../../utils/amountFormat';
import { Pages } from '../../../App';
import { ConfigContext } from '../../../providers/ConfigContext';
import BigNumber from 'bignumber.js';

interface Props {
	setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
	selectedEwallet: EwalletInfo;
	setSelectedTransaction: React.Dispatch<React.SetStateAction<TransactionInfoTax>>;
}

const [listTransactions] = getWalletTransaction();

const EwalletChooseTransactionContent = ({ setCurrentPage, selectedEwallet, setSelectedTransaction }: Props) => {
	const { apiBase } = useContext(ConfigContext);

	const [transactions, setTransaction] = useState<TransactionInfoTax[]>([]);
	const [transactionId, setTransactionId] = useState<string>('');

	const createTime = (time: string) => {
		let timer;
		const final = `${time.substring(5, 7)}/${time.substring(8, 10)}/${time.substring(0, 4)}`;
		if (parseInt(time.substring(11, 13), 10) > 12) {
			timer = `${parseInt(time.substring(11, 13), 10) % 12}:${time.substring(14, 16)}PM`;
		} else {
			timer = `${time.substring(11, 13)}:${time.substring(14, 16)}AM`;
		}
		return `${final}, ${timer}`;
	};

	useEffect(() => {
		const walletTransactions = async () => {
			const { data, error } = await listTransactions(apiBase, selectedEwallet.id);
			if (error) {
				console.error(error);
				setCurrentPage(Pages.Error);
			} else if (data != null) {
				for (const transaction of data) {
					transaction.saleAmount = BigNumber(transaction.saleAmount);
					transaction.refundableAmount = BigNumber(transaction.refundableAmount);
					transaction.refundableAmountLessTax = BigNumber(transaction.refundableAmountLessTax);
					transaction.tax.amount = BigNumber(transaction.tax.amount);
					transaction.tax.rate = BigNumber(transaction.tax.rate);
					transaction.tax.taxableSubtotal = BigNumber(transaction.tax.taxableSubtotal);
				}
				setTransaction(data);
			}
		};

		walletTransactions();
	}, [selectedEwallet.id, setCurrentPage, apiBase]);

	const navigateToeWalletReview = () => {
		const transaction = transactions.find((transaction: TransactionInfoTax) => transaction.id === transactionId);
		if (transaction && selectedEwallet) {
			window._satellite.track('choose-refund-choose-amount');
			setSelectedTransaction(transaction);
			setCurrentPage(Pages.eWalletRefundReview);
		} else {
			setCurrentPage(Pages.Error);
		}
	};

	return (
		<>
			<TopBackNavBar setCurrentPage={setCurrentPage} title="Choose Transaction" location={Pages.eWalletRefund} />
			<EwalletPage>
				<PageWrapper>
					<EwalletDiv>
						<EwalletCard item={selectedEwallet} />
					</EwalletDiv>
					<TransactionDiv>
						<Styledheading>Choose Transaction</Styledheading>
						<StyledBodyText>Only payments made in the Payment Portal can be refunded.</StyledBodyText>
						<EwalletWrapper>
							<ResponsiveContainer>
								{transactions.map(
									(item: TransactionInfoTax, index: number) =>
										!item.refundableAmount.isZero() && (
											<div key={item.id}>
												<Label>
													<RadioButton
														type="radio"
														name="radioChoice"
														value={item.id}
														onClick={() => {
															setTransactionId(item.id);
														}}
													/>
													<RefundInfo>
														<TimeInfo>
															<SaleInfo>
																<b>Sale</b> | {createTime(item.createdOn)}
															</SaleInfo>
														</TimeInfo>
														<AmountInfo>-{formatCurrencyString(item.refundableAmount.toFixed(2))}</AmountInfo>
													</RefundInfo>
												</Label>
												{!item.refundableAmount.eq(item.saleAmount) && <PartialText>Partially Refunded</PartialText>}
												{index !== transactions?.length - 1 && <HorizontalLineBreak />}
											</div>
										)
								)}
							</ResponsiveContainer>
						</EwalletWrapper>
					</TransactionDiv>
				</PageWrapper>
				<StyledRedButton disabled={transactionId === ''} handleClick={navigateToeWalletReview}>
					Choose Amount
				</StyledRedButton>
			</EwalletPage>
		</>
	);
};

const EwalletPage = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
`;

const PageWrapper = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	margin: auto;
	gap: 45px;
	margin-top: 45px;
	margin-bottom: 20px;

	@media only screen and (max-width: 760px) {
		margin-top: 24px;
		gap: 30px;
		flex-direction: column;
	}
`;

const EwalletDiv = styled.div`
	width: 280px;
`;

const TransactionDiv = styled.div`
	width: 360px;
	text-align: left;
	height: 100%;

	@media only screen and (max-width: 760px) {
		width: 280px;
	}
`;

const StyledRedButton = styled(RedButton)`
	width: 280px;
	margin-bottom: 45px;
`;

const Styledheading = styled.h1`
	font-size: 24px;
	font-style: normal;
	font-weight: 400;
	line-height: 28px;
	color: #333;
	margin: 0;
`;

const StyledBodyText = styled.p`
	font-size: 13px;
	font-style: normal;
	font-weight: 400;
	line-height: 15px;
	color: #333;
	margin: 12px 0 8px 0;
`;

const HorizontalLineBreak = styled.div`
	width: 100%;
	height: 1px;
	background: #d7d7d7;
	margin: 25px 0 25px 0;
`;

const EwalletWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	margin: 25px 0 25px 0;
`;

const ResponsiveContainer = styled.div`
	width: 100%;
	display: flex;
	justify-content: space-between;
	margin: auto;
	flex-direction: column;
`;

const Label = styled.label`
	display: flex;
	flex-direction: row;
	border-radius: 3px;
	width: 100%;
`;

const RadioButton = styled.input`
	padding-left: 8px;
	border-radius: 4px;
	width: 20px;
	height: 20px;
`;

const RefundInfo = styled.div`
	display: flex;
	justify-content: space-between;
	width: 100%;
`;

const TimeInfo = styled.div`
	display: flex;
	align-items: center;
	padding-left: 5px;
	width: 80%;
`;

const AmountInfo = styled.p`
	font-size: 24px;
	font-style: normal;
	font-weight: 400;
	line-height: 28px;
	margin: 0;
	padding-left: 5px;

	@media only screen and (max-width: 760px) {
		font-size: 21px;
	}
`;

const SaleInfo = styled.p`
	font-size: 13px;
	font-style: normal;
	font-weight: 400;
	line-height: 15px;
	margin: 0;

	b {
		font-weight: 600;
	}

	@media only screen and (max-width: 760px) {
		font-size: 12px;
	}
`;

const PartialText = styled.p`
	padding-left: 30px;
	margin-top: -0.5em;
	font-weight: 300;
	font-size: 12px;
	margin: 0;

	@media only screen and (max-width: 760px) {
		font-size: 11px;
	}
`;

export default EwalletChooseTransactionContent;
